import {
  BeneficiaryLegalStatus,
  BeneficiaryLegalStatusTranslationKey
} from "@/modules/transaction/common/enum";
import type { IAccountTransfer } from "@/modules/transaction/common/types";
import { getBankName } from "@/modules/transaction/helpers";
import { Currencies } from "@/shared/common/enums";
import { Amount } from "@/shared/components";
import { cleanupAmount } from "@/shared/helpers";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { type RefObject, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  transfer: IAccountTransfer;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const AccountTransferDetailRows = ({
  transfer,
  scrollbarContainerRef
}: Props) => {
  const { t } = useTranslation();

  const totalFee = useMemo(() => {
    if (transfer.urgentTransferCommissionAmount) {
      return Number(cleanupAmount(`${transfer.commissionAmount}`)) +
        Number(cleanupAmount(`${transfer.urgentTransferCommissionAmount}`));
    }
    return 0;
  }, [transfer]);

  const bankName = useMemo(() => {
    return t(getBankName(transfer.creditAccount));
  }, [transfer]);

  return (
    <div>
      <div>
        <CollapseGroup
          className="detail-collapse"
          items={[
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.receiverAccount")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.creditAccount as string}
                        />
                        <Text size="medium" weight="bold">{transfer.creditAccount}</Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  {bankName && (
                    <div>
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.beneficiaryBank")}
                        </Text>
                        <Tooltip
                          position={Positions.TOP_CENTER}
                          id="bankName-tooltip"
                          text={bankName}
                        />
                        <Text
                          id="bankName-tooltip"
                          className="truncate cursor-pointer pl-16"
                          size="medium"
                          weight="bold"
                        >
                          {bankName}
                        </Text>
                      </div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                    </div>
                  )}
                  {!!transfer?.beneficiaryLegalStatus &&
                    transfer?.beneficiaryLegalStatus !== BeneficiaryLegalStatus.NotSet && (
                      <div>
                        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                          <Text
                            className="text-nowrap"
                            type="secondary"
                            size="medium"
                            weight="semibold"
                          >
                            {t("transfer.beneficiaryLegalStatus")}
                          </Text>
                          <Text
                            className="truncate cursor-pointer pl-16"
                            size="medium"
                            weight="bold"
                          >
                            {
                              t(`general.${BeneficiaryLegalStatusTranslationKey[
                                transfer?.beneficiaryLegalStatus as keyof typeof BeneficiaryLegalStatusTranslationKey
                                ]}`
                              )
                            }
                          </Text>
                        </div>
                        <Divider
                          color="light"
                          type="primary"
                          isHorizontal
                        />
                      </div>
                    )}
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.beneficiaryName")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="account-transfer-beneficiary-tooltip"
                        text={transfer.beneficiary as string}
                      />
                      <Text
                        size="medium"
                        weight="bold"
                        className="truncate cursor-pointer pl-16"
                        id="account-transfer-beneficiary-tooltip"
                      >
                        {transfer.beneficiary}
                      </Text>
                    </div>
                  </div>
                </>
              ),
              value: "beneficiary_details",
              isOpen: true,
              title: t("general.beneficiaryDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.accountFrom")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.debitAccount?.number as string}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.debitAccount?.number}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.amount")}
                      </Text>
                      <Amount
                        currency={transfer.currency}
                        amount={transfer.amount as number}
                      />
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.purpose")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="account-transfer-purpose-tooltip"
                        text={transfer.purpose}
                      />
                      <Text
                        id="account-transfer-purpose-tooltip"
                        className="truncate cursor-pointer pl-16"
                        size="medium"
                        weight="bold"
                      >
                        {transfer.purpose}
                      </Text>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.commissionFee")}
                      </Text>
                      <Amount
                        currency={Currencies.AMD}
                        amount={transfer.commissionAmount}
                      />
                    </div>

                    {!!transfer.urgentTransferCommissionAmount && (
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                    )}
                  </div>
                  {!!transfer.urgentTransferCommissionAmount && (
                    <>
                      <div>
                        <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                          <Text
                            className="text-nowrap"
                            type="secondary"
                            size="medium"
                            weight="semibold"
                          >
                            {t("general.urgentCommissionFee")}
                          </Text>
                          <Amount
                            currency={Currencies.AMD}
                            amount={transfer.urgentTransferCommissionAmount}
                          />
                        </div>
                        <Divider
                          color="light"
                          type="primary"
                          isHorizontal
                        />
                      </div>
                      <div>
                        <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                          <Text
                            className="text-nowrap"
                            type="secondary"
                            size="medium"
                            weight="semibold"
                          >
                            {t("transfer.totalFee")}
                          </Text>
                          <Amount
                            currency={Currencies.AMD}
                            amount={totalFee}
                          />
                        </div>
                        <Divider
                          color="light"
                          type="primary"
                          isHorizontal
                        />
                      </div>
                    </>
                  )}
                  {transfer.commissionAccount?.number && (
                    <div>
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.feeFrom")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Copy
                            textAfterCopy={t("general.copied")}
                            size="medium"
                            parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                            text={transfer.commissionAccount?.number}
                          />
                          <Text size="medium" weight="bold">
                            {transfer.commissionAccount?.number}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ),
              value: "transfer_details",
              isOpen: true,
              title: t("transfer.transferDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.transactionNumber")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.transactionNumber as string}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.transactionNumber}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text type="secondary" size="medium" weight="semibold">
                        {t("general.date")}
                      </Text>
                      <Text size="medium" weight="bold">
                        {transfer.scheduleDate}
                      </Text>
                    </div>
                  </div>
                </>
              ),
              value: "other_details",
              isOpen: true,
              title: t("general.otherDetails")
            },
          ]}
        />
      </div>
    </div>
  );
};
